import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import Pagination from "../../../Pagination";
import { cardOutline, headingFonts } from "../../../../Styles/Styles";
import LoadingAnimation from "../../../Animations/LoadingAnimation";

export default function ImagesManagement() {
  const [images, setImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleting, setDeleting] = useState(false);
  const limit = 100; // Number of images per page

  // Fetch paginated images from backend API
  const fetchImages = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/images/ad-images?page=${page}&limit=${limit}`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to load images");
      }

      setImages(data.images);
      setTotalPages(data.totalPages);
      setCurrentPage(page);
      setSelectedImages([]); // Reset selected images on fetch
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages(currentPage);
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle individual image selection
  const handleSelectImage = (imageUrl) => {
    setSelectedImages((prevSelected) =>
      prevSelected.includes(imageUrl)
        ? prevSelected.filter((url) => url !== imageUrl)
        : [...prevSelected, imageUrl]
    );
  };

  // Handle bulk delete
  const handleDeleteSelected = async () => {
    if (deleting || selectedImages.length === 0) return; // Prevent multiple requests
    setDeleting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/images/delete-multiple`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ imageUrls: selectedImages }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to delete images");
      }

      // Refresh images after deletion
      fetchImages(currentPage);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" sx={{ ...headingFonts }}>
          Images Management
        </Typography>
        <IconButton onClick={() => fetchImages(currentPage)} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      )}

      {!loading && !error && (
        <>
          {/* Bulk Delete Button */}
          {selectedImages.length > 0 && (
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteSelected}
                disabled={deleting}
                startIcon={<DeleteIcon />}
              >
                Delete Selected ({selectedImages.length})
              </Button>
            </Box>
          )}

          <TableContainer sx={{ ...cardOutline }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headingFonts }}>Select</TableCell>
                  <TableCell sx={{ ...headingFonts }}>Image</TableCell>
                  <TableCell sx={{ ...headingFonts }}>Ad Name</TableCell>
                  <TableCell sx={{ ...headingFonts }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {images.map((image, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Checkbox
                        checked={selectedImages.includes(image.imageUrl)}
                        onChange={() => handleSelectImage(image.imageUrl)}
                      />
                    </TableCell>
                    <TableCell>
                      <img
                        src={image.imageUrl}
                        alt={image.adName}
                        style={{ width: 100, height: 100, borderRadius: 8 }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...headingFonts }}>
                      {image.adName}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleSelectImage(image.imageUrl)}
                        color="error"
                        disabled={deleting}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
